import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  inject,
  Input,
  OnInit,
  Optional,
  Output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { marker as extractTranslation } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import { MEMBERSHIP_GEO_COUNTRY_API_SERVICE_TOKEN, MembershipGeoCountryApiService } from '@nk/api-lib';
import { GENERIC_AUTH_SERVICE_TOKEN } from '@nk/auth-lib';
import {
  CompanyPersonSearchWithDistanceAndSearchType,
  GeoCountryEntity,
  SearchQueryMethod,
  SimpleSearchEntity,
  SimpleSearchWithDistanceEntity,
} from '@nk/data-entities-lib';
import { ClickOutsideDirective } from '@nk/ui-lib';
import { arrayUnique } from '@nk/utils-lib';
import { BehaviorSubject, combineLatest, EMPTY, Observable, of } from 'rxjs';
import { catchError, debounceTime, filter, map, startWith, switchMap, tap } from 'rxjs/operators';
import { CompanyPersonSearchService } from '../../services/company-person-search.service';
import { CompanyPersonSearchResultsComponent } from '../company-person-search-results/company-person-search-results.component';

function sortPersonsFirst(
  aggregatedSearchResults: CompanyPersonSearchWithDistanceAndSearchType[] = [],
): CompanyPersonSearchWithDistanceAndSearchType[] {
  return aggregatedSearchResults.sort((a, b) => {
    if (a.person_uuid && !b.person_uuid) {
      return 1;
    } else if (b.person_uuid && !a.person_uuid) {
      return -1;
    }
    return 0;
  });
}

@Component({
  selector: 'nk-company-person-search-box',
  imports: [FormsModule, ClickOutsideDirective, CompanyPersonSearchResultsComponent],
  templateUrl: './company-person-search-box.component.html',
  styleUrls: ['./company-person-search-box.component.scss'],
})
export class CompanyPersonSearchBoxComponent implements OnInit {
  // Style options
  @Input() public placeholder = '';
  @Input() public disabled = false; // makes it impossible to write into input field.
  @Input() public andClass = ''; // For adding additional classes to the outer component
  @Input() public andInputClass = ''; // For adding additional classes to the input field
  @Input() public customIcon = ''; // like: fa-plus  Todo implement
  @Input() public clearOnSelect = false; // Nice for where you want to click to add multiple companies // TODO: test

  // Search query options:
  @Input() public geoCountryIdentifiersOverride: string[] | undefined;
  @Input() public includeDeadCompanies = true; // TODO: tEST
  @Input() public searchEntityType: 'all' | 'onlyPersons' | 'onlyCompanies' = 'all';
  @Input() public searchBoxStyle: 'round' | 'square' = 'round';

  // screening: todo fix
  @Input() public dontShowResults = false; // Used if you want to style your own results.
  @Output() public dataSourceOutput = new EventEmitter<SimpleSearchWithDistanceEntity[]>(); // outputs the aggregatedSearchResults when its changed.
  // output on click.
  @Output() public simpleSearchEnterKeyPressOutput = new EventEmitter<string>(); // TODO: delete?
  @Output() public searchSelectOutput = new EventEmitter<SimpleSearchEntity>();

  // styling
  public openDropdown = false;
  public selectedItemIndex = 0;

  public loading = false;
  public loadingQuick = false;

  public simpleSearchInput = '';
  private _simpleSearchChange: BehaviorSubject<string> = new BehaviorSubject('');
  public readonly simpleSearchChange$: Observable<string> = this._simpleSearchChange.asObservable();

  public aggregatedSearchResults: CompanyPersonSearchWithDistanceAndSearchType[] = [];
  private _claimedGeoCountryPreference$!: Observable<string[]>;

  // private _membershipGeoCountryService = inject(MEMBERSHIP_GEO_COUNTRY_API_SERVICE_TOKEN);
  private _searchService = inject(CompanyPersonSearchService);
  private _authService = inject(GENERIC_AUTH_SERVICE_TOKEN);
  private _translateService = inject(TranslateService);
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    @Optional()
    @Inject(MEMBERSHIP_GEO_COUNTRY_API_SERVICE_TOKEN)
    private _membershipGeoCountryService: MembershipGeoCountryApiService,
  ) {}

  public onInputChange(searchQuery: string): void {
    this.simpleSearchInput = searchQuery;
    this._simpleSearchChange.next(this.simpleSearchInput);

    this.selectedItemIndex = 0;
    this.openDropdown = true;
    if (searchQuery.length === 0) {
      this.aggregatedSearchResults = [];
      this.dataSourceOutput.emit(this.aggregatedSearchResults);
    }
  }

  public onSearchSelect(event: SimpleSearchEntity): void {
    this.searchSelectOutput.emit(event);
    this.openDropdown = false;
    if (this.clearOnSelect) {
      this.simpleSearchInput = '';
      this.aggregatedSearchResults = [];
      this._simpleSearchChange.next('');
    }
  }

  private _addSearchTypeToResults(
    resultList: SimpleSearchWithDistanceEntity[],
    searchType: SearchQueryMethod,
  ): CompanyPersonSearchWithDistanceAndSearchType[] {
    return resultList.map((item) => {
      const res: CompanyPersonSearchWithDistanceAndSearchType = {
        ...item,
        searchType,
      };
      return res;
    });
  }

  public ngOnInit(): void {
    if (!this.placeholder) {
      this.placeholder = this._translateService.instant(
        extractTranslation('simple_search.search_for_company'),
      ) as string;
    }
    this._claimedGeoCountryPreference$ = this._authService.authInfo$.pipe(
      switchMap((authInfo) => {
        if (authInfo.isValid()) {
          return this._membershipGeoCountryService.appMembershipGeoCountryPreference$;
        } else {
          return of([]);
        }
      }),
      map((geoCountries: GeoCountryEntity[]) => {
        let geoCountriesToUse: string[];
        const geoCountryIdentifiersOverride = this.geoCountryIdentifiersOverride;
        if (geoCountryIdentifiersOverride) {
          geoCountriesToUse = geoCountries
            .filter((a) => {
              return geoCountryIdentifiersOverride.includes(a.country_code_two);
            })
            .map((a) => a.country_code_two);
        } else {
          geoCountriesToUse = geoCountries.map((a) => a.country_code_two);
        }
        return geoCountriesToUse;
      }),
    );

    this.simpleSearchChange$
      .pipe(
        map((searchQuery) => searchQuery.trim()),
        tap((searchQuery) => {
          this.loading = searchQuery.length !== 0;
          this.loadingQuick = searchQuery.length !== 0;
          this._cdr.markForCheck();
        }),
        debounceTime(250),
        filter((searchQuery) => searchQuery.length > 0),
        switchMap((searchQuery) =>
          combineLatest([
            this._getSimpleSearchData(searchQuery, SearchQueryMethod.quick),
            this._getSimpleSearchData(searchQuery, SearchQueryMethod.fuzzy),
          ]),
        ),
      )
      .subscribe(([quick, fuzzy]) => {
        this.loadingQuick = quick === null;
        this.loading = quick === null || fuzzy === null;
        if (quick || fuzzy) {
          const sortedQuick = quick ? sortPersonsFirst(quick) : [];
          const aggregatedSearchResults = [...sortedQuick, ...(fuzzy || [])];
          this.aggregatedSearchResults = arrayUnique(
            aggregatedSearchResults,
            (a) => a.company_uuid + '--' + a.person_uuid,
          );
          this.dataSourceOutput.emit(this.aggregatedSearchResults);
        }
        this._cdr.markForCheck();
      });
  }

  public onKeyDown(event: KeyboardEvent): void {
    this.openDropdown = true;
    if (this.loadingQuick) {
      return; // THis is so that if user clicks "enter" too fast it will not open old results
    }

    if (
      event.key.toLowerCase() === 'enter' &&
      this.aggregatedSearchResults &&
      this.aggregatedSearchResults[this.selectedItemIndex]
    ) {
      this.openDropdown = false;
      if (this.aggregatedSearchResults.length > 0) {
        this.searchSelectOutput.emit(this.aggregatedSearchResults[this.selectedItemIndex]);
      }
    }

    this.selectedItemIndex = this._getSelectedItemIndex(event, this.selectedItemIndex, this.aggregatedSearchResults);
  }

  private _getSelectedItemIndex(
    event: KeyboardEvent,
    selectedItemIndex: number,
    aggregatedSearchResults: CompanyPersonSearchWithDistanceAndSearchType[],
  ): number {
    if (event.key === 'ArrowDown' || event.key === 'Tab') {
      if (selectedItemIndex >= aggregatedSearchResults.length - 1) {
        selectedItemIndex = 0;
      } else {
        selectedItemIndex++;
      }
    }

    if (event.key === 'ArrowUp') {
      if (this.selectedItemIndex === 0) {
        selectedItemIndex = aggregatedSearchResults.length - 1;
      } else if (this.selectedItemIndex < aggregatedSearchResults.length + 1) {
        selectedItemIndex--;
      } else {
        selectedItemIndex = 0;
      }
    }
    return selectedItemIndex;
  }

  private _getSimpleSearchData(
    searchQuery: string,
    searchQueryMethod: SearchQueryMethod,
  ): Observable<CompanyPersonSearchWithDistanceAndSearchType[] | null> {
    const limit = 10;

    return this._claimedGeoCountryPreference$.pipe(
      switchMap((geoCountryCode) => {
        return this._searchService.search(
          searchQuery,
          limit,
          searchQueryMethod,
          this.includeDeadCompanies,
          geoCountryCode,
          this.searchEntityType === 'onlyPersons',
          this.searchEntityType === 'onlyCompanies',
        );
      }), // TODO: filter out persons?
      startWith(null), // TODO: CodeReview - we start here with null to make observable emit it as a value
      map((data) => (data ? this._addSearchTypeToResults(data, searchQueryMethod) : null)),
      catchError((error: unknown) => {
        console.error(error);
        return EMPTY;
      }),
    );
  }

  public onClickOutside(): void {
    this.openDropdown = false;
  }
}
